import styled from 'styled-components';

interface TabDef {
	id: string;
	label: string;
	count?: number | null;
}

interface Props {
	activeTab: string;
	onTabChange?: (tabId: string) => void;
	tabs?: TabDef[];
}

const DEFAULT_TABS: TabDef[] = [
	{id: 'overview', label: 'Overview'},
	{id: 'activity', label: 'Activity'},
	{id: 'stream', label: 'Stream'},
	{id: 'attachments', label: 'Attachments'},
	{id: 'tasks', label: 'Tasks'},
	{id: 'related', label: 'Related'},
];

const TabBar = styled.div`
	border-bottom: 1px solid ${p => p.theme.ticketing.color.dark100};
`;
const TabNav = styled.nav`
	display: flex;
	gap: 24px;
	margin-bottom: -1px;
`;
const TabButton = styled.button<{$active: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 12px 4px;
	font: ${p => p.theme.ticketing.font.label1};
	border: 0;
	border-bottom: 2px solid ${p => (p.$active ? p.theme.ticketing.color.brand500 : 'transparent')};
	color: ${p => (p.$active ? p.theme.ticketing.color.brand500 : p.theme.ticketing.color.dark500)};
	background: transparent;
	cursor: pointer;
	transition: color 0.15s, border-color 0.15s;

	&:hover {
		color: ${p => (p.$active ? p.theme.ticketing.color.brand500 : p.theme.ticketing.color.dark700)};
		border-bottom-color: ${p => (p.$active ? p.theme.ticketing.color.brand500 : '#cbd5e1')};
	}
`;
const CountPill = styled.span<{$active: boolean}>`
	padding: 2px 6px;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => (p.$active ? p.theme.ticketing.color.brand50 : p.theme.ticketing.color.bgSubtle)};
	color: ${p => (p.$active ? p.theme.ticketing.color.brand500 : p.theme.ticketing.color.dark500)};
`;
const IconSvg = styled.svg`
	width: 16px;
	height: 16px;
`;

function TabIcon({id}: {id: string}) {
	switch (id) {
		case 'overview':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
					/>
				</IconSvg>
			);
		case 'activity':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
				</IconSvg>
			);
		case 'stream':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
					/>
				</IconSvg>
			);
		case 'attachments':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
					/>
				</IconSvg>
			);
		case 'tasks':
		case 'time':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					/>
				</IconSvg>
			);
		case 'related':
			return (
				<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.02a4.5 4.5 0 00-1.242-7.244l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757"
					/>
				</IconSvg>
			);
		default:
			return null;
	}
}

export function TicketTabs({activeTab, onTabChange, tabs = DEFAULT_TABS}: Props) {
	return (
		<TabBar>
			<TabNav aria-label="Ticket tabs">
				{tabs.map(tab => {
					const isActive = tab.id === activeTab;
					return (
						<TabButton
							key={tab.id}
							type="button"
							$active={isActive}
							onClick={() => onTabChange?.(tab.id)}
							aria-current={isActive ? 'page' : undefined}
						>
							<TabIcon id={tab.id} />
							{tab.label}
							{tab.count != null && <CountPill $active={isActive}>{tab.count}</CountPill>}
						</TabButton>
					);
				})}
			</TabNav>
		</TabBar>
	);
}
